import { formatCurrency, formatTwoDigitNumber } from "@/common/utils";
import { PRODUCT } from "@/constants/common.constants";
import {
  PREVIOUS_CLAIM_CLAIMED_UNDER_ITEMS,
  PREVIOUS_CLAIM_TYPE_OF_LOSS_ITEMS,
  setItemDataUiids,
} from "@/constants/component.constants";
import store from "@/store/index";
import { QuestionStore } from "@/store/store";
import moment from "moment";

const NO_CLAIMS_CONTENTS = "NoClaimsContents";
const NO_CLAIMS_BUILDINGS = "NoClaimsBuildings";

export const showNoClaims = (sectionQuestion, apiAnswers) => {
  if (
    !sectionQuestion.InterfaceKey ||
    (sectionQuestion.InterfaceKey !== NO_CLAIMS_CONTENTS &&
      sectionQuestion.InterfaceKey !== NO_CLAIMS_BUILDINGS)
  )
    return;

  // Only explicitly set value to true; answer may be defaulted but we still want to show claims Qs
  if (
    sectionQuestion.InterfaceKey === NO_CLAIMS_CONTENTS &&
    apiAnswers.some((q) => q.InterfaceKey === NO_CLAIMS_CONTENTS)
  ) {
    sectionQuestion.IsVisible = true;
  } else if (
    sectionQuestion.InterfaceKey === NO_CLAIMS_BUILDINGS &&
    apiAnswers.some((q) => q.InterfaceKey === NO_CLAIMS_BUILDINGS)
  ) {
    sectionQuestion.IsVisible = true;
  }
};

export const populateClaimTable = (
  state,
  quoteQuestions,
  tableQuestions = null
) => {
  const isWithPreviousClaim = quoteQuestions.find(
    (question) => question.InterfaceKey === "PreviousClaim"
  )?.Value;
  if (!isWithPreviousClaim || isWithPreviousClaim !== "True") return;

  const allClaims = quoteQuestions.filter((question) => {
    if (
      question.InterfaceKey.includes("PreviousClaim") &&
      "RepeatingQuestionSetIndex" in question
    ) {
      return question;
    }
  });

  const groups = allClaims.reduce((groups, item) => {
    const group = groups[item.RepeatingQuestionSetIndex] || [];
    group.push(item);
    groups[item.RepeatingQuestionSetIndex] = group;
    return groups;
  }, []);

  let claims = [];
  groups.forEach((group) => {
    claims.push(
      group.filter(
        (v, i, a) =>
          a.findIndex((v2) => v2.InterfaceKey === v.InterfaceKey) === i
      )
    );
  });

  const layoutTableBuilder =
    tableQuestions ??
    state.form.sections[0].Questions.find(
      (q) => q.AnswerControlType === "LayoutTableBuilder"
    );

  if (!layoutTableBuilder) return;

  let claimTableQuestions = [];
  if (
    QuestionStore.getters("productId") === PRODUCT.LandLord ||
    (QuestionStore.getters("productId") === PRODUCT.Home &&
      !QuestionStore.getters("isSelfServe")) ||
    QuestionStore.getters("productId") === PRODUCT.Tenants_Contents
  ) {
    const previousClaimClaimedUnderOptions = setItemDataUiids(
      "PreviousClaimClaimedUnder",
      PREVIOUS_CLAIM_CLAIMED_UNDER_ITEMS.map((x) => ({
        ...x,
        Value: x.value, // adding Value field to reuse logic formatClaimData
      }))
    );
    const previousClaimTypeOfLossOptions = setItemDataUiids(
      "PreviousClaimTypeOfLoss",
      PREVIOUS_CLAIM_TYPE_OF_LOSS_ITEMS.map((x) => ({
        ...x,
        Value: x.value,
      })) // adding Value field to reuse logic formatClaimData
    );

    claimTableQuestions = [
      { Options: previousClaimClaimedUnderOptions },
      { Options: previousClaimTypeOfLossOptions },
    ];
  } else {
    claimTableQuestions = layoutTableBuilder.Questions;
    claimTableQuestions.forEach((question) => {
      question.Value = "";
    });
  }

  const claimsTableData = layoutTableBuilder.Value;
  claims.forEach((claim) => {
    claimsTableData.push(formatClaimData(claim, claimTableQuestions));
    state.form.repeatingQuestionSetIndex++;
  });
};

export function preparePreviousClaimData(
  previousClaim,
  previousClaimValues,
  repeatingQuestionSetIndex,
  previousClaimItems
) {
  if (!previousClaim) {
    return;
  }
  previousClaimValues.Value = [];
  let repeatingQuestionSetIndexClaim = repeatingQuestionSetIndex;
  previousClaimItems.forEach((item) => {
    repeatingQuestionSetIndexClaim++;
    const value = {};
    for (const fieldName in item) {
      value[fieldName] = {
        InterfaceKey: fieldName,
        Value: item[fieldName].Value?.Value
          ? item[fieldName].Value.Value
          : item[fieldName].Value,
        RepeatingQuestionSetIndex: repeatingQuestionSetIndexClaim,
      };
    }
    previousClaimValues.Value.push(value);
    store.dispatch("questions/increaseRepeatingQuestionSetIndex");
  });
}

export function formatClaimData(claim, claimTableQuestions) {
  const claimTemplate = {
    PreviousClaimDateOfLoss: {},
    PreviousClaimAmountOfLoss: {},
    PreviousClaimClaimedUnder: {},
    PreviousClaimTypeOfLoss: {},
  };

  claim.forEach((claim) => {
    if (claim.InterfaceKey === "PreviousClaimAmountOfLoss") {
      claimTemplate.PreviousClaimAmountOfLoss.Value = claim.Value;
      claimTemplate.PreviousClaimAmountOfLoss.RepeatingQuestionSetIndex =
        claim.RepeatingQuestionSetIndex;
      claimTemplate.PreviousClaimAmountOfLoss.InterfaceKey = claim.InterfaceKey;
      claimTemplate.PreviousClaimAmountOfLoss.Text = formatCurrency(
        claim.Value
      );
    }

    if (claim.InterfaceKey === "PreviousClaimDateOfLoss") {
      const date = moment(claim.Value, "DD/MM/YYYY");
      if (date.isValid()) {
        const formattedDate = `${formatTwoDigitNumber(
          date.date().toString()
        )}/${formatTwoDigitNumber((date.month() + 1).toString())}/${date
          .year()
          .toString()}`;
        claimTemplate.PreviousClaimDateOfLoss.Value = formattedDate;
        claimTemplate.PreviousClaimDateOfLoss.Text = formattedDate;
      }
      claimTemplate.PreviousClaimDateOfLoss.RepeatingQuestionSetIndex =
        claim.RepeatingQuestionSetIndex;
      claimTemplate.PreviousClaimDateOfLoss.InterfaceKey = claim.InterfaceKey;
    }

    if (claim.InterfaceKey === "PreviousClaimClaimedUnder") {
      let PreviousClaimClaimedUnderValue = claimTableQuestions[0].Options.find(
        (option) => option.Value === claim.Value
      );
      claimTemplate.PreviousClaimClaimedUnder.Value =
        PreviousClaimClaimedUnderValue;
      claimTemplate.PreviousClaimClaimedUnder.Text =
        PreviousClaimClaimedUnderValue.text;
      claimTemplate.PreviousClaimClaimedUnder.RepeatingQuestionSetIndex =
        claim.RepeatingQuestionSetIndex;
      claimTemplate.PreviousClaimClaimedUnder.InterfaceKey = claim.InterfaceKey;
    }

    if (claim.InterfaceKey === "PreviousClaimTypeOfLoss") {
      let PreviousClaimTypeOfLossValue = claimTableQuestions[1].Options.find(
        (option) => option.Value === claim.Value
      );
      claimTemplate.PreviousClaimTypeOfLoss.Value =
        PreviousClaimTypeOfLossValue;
      claimTemplate.PreviousClaimTypeOfLoss.Text =
        PreviousClaimTypeOfLossValue.text;
      claimTemplate.PreviousClaimTypeOfLoss.RepeatingQuestionSetIndex =
        claim.RepeatingQuestionSetIndex;
      claimTemplate.PreviousClaimTypeOfLoss.InterfaceKey = claim.InterfaceKey;
    }
  });

  return claimTemplate;
}
